import React from 'react';
import Skeleton from '@mui/material/Skeleton';
import { useStyles } from './RequestAQuoteSkeleton.styles';
import { Grid, useMediaQuery, useTheme } from '@mui/material';
import {
  IRAQConfiguration,
  VehicleSelectorType,
} from 'interfaces/requestAQuote';

interface IRequestAQuoteSkeleton {
  configuration?: IRAQConfiguration;
  title?: string;
  description?: string;
}
const RequestAQuoteSkeleton = ({
  configuration,
  title,
  description,
}: IRequestAQuoteSkeleton): JSX.Element => {
  const classes = useStyles();
  const theme = useTheme();
  const matchesMobileMidBreakpoint = useMediaQuery(
    theme.breakpoints.up('viewportMobileMid'),
    { noSsr: true }
  );
  const matchesMediumBreakpoint = useMediaQuery(theme.breakpoints.up('md'), {
    noSsr: true,
  });
  const isFormDisabled = !!(
    configuration &&
    configuration.modelYear &&
    configuration.modelSeries &&
    configuration.modelId
  );
  const vehicleSelectorType =
    configuration?.variant || VehicleSelectorType.basic;
  return (
    <div className={classes.root}>
      {(title || description) && (
        <div className={classes.header}>
          <Skeleton variant="rectangular" className={classes.headerTitle} />
          {[
            ...Array(
              matchesMediumBreakpoint ? 1 : matchesMobileMidBreakpoint ? 2 : 3
            ),
          ].map((value: undefined, index: number) => (
            <Skeleton
              variant="rectangular"
              key={index}
              className={classes.headerDescription}
            />
          ))}
        </div>
      )}
      <div className={classes.container}>
        <Skeleton variant="rectangular" className={`${classes.step} partial`} />
        <div className={classes.step1Container}>
          <Grid className={classes.stepContainer}>
            <Skeleton
              variant="rectangular"
              className={`${classes.zipInput} partial`}
            />
            <Skeleton
              variant="rectangular"
              className={`${classes.filter} partial`}
            />
            <Grid container className={classes.dealerTabs}>
              <Skeleton
                variant="rectangular"
                className={`${classes.dealerTab} ${classes.dealerTabActive}`}
              />
              <Skeleton variant="rectangular" className={classes.dealerTab} />
            </Grid>
            <Grid
              container
              direction="row"
              className={classes.dealerInfoContainer}
            >
              <Grid item xs={12}>
                <Skeleton
                  variant="rectangular"
                  className={classes.dealerCard}
                />
              </Grid>
              <Grid item xs={12}>
                <Skeleton
                  variant="rectangular"
                  className={classes.dealerCard}
                />
              </Grid>
              <Grid item xs={12}>
                <Skeleton
                  variant="rectangular"
                  className={classes.dealerCard}
                />
              </Grid>
              <Grid item xs={12}>
                <Skeleton
                  variant="rectangular"
                  className={classes.changeDealerButton}
                />
              </Grid>
            </Grid>
          </Grid>
          <Skeleton variant="rectangular" className={classes.dealersMap} />
        </div>
      </div>
      <div className={classes.container}>
        <Skeleton variant="rectangular" className={`${classes.step} partial`} />
        <Grid
          container
          direction="row"
          className={classes.vehicleDataContainer}
        >
          <Grid item xs={12} md={6} className={classes.dropdownContainer}>
            <Skeleton variant="text" className={classes.dropdownTitle} />
            {!isFormDisabled && <Skeleton className={classes.taxonomyTab} />}
            <Skeleton variant="rectangular" className={classes.dropdown} />
            <Skeleton variant="text" className={classes.dropdownTitle} />
            <Skeleton variant="rectangular" className={classes.dropdown} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton
              variant="rectangular"
              className={`${classes.vehicleCard} ${vehicleSelectorType}`}
            />
          </Grid>
        </Grid>
      </div>
      <div className={classes.container}>
        <Skeleton variant="rectangular" className={`${classes.step}`} />
        {!matchesMediumBreakpoint && ( //mobile view
          <Grid container direction="row">
            {[...Array(6)].map((value: undefined, index: number) => {
              return (
                <Grid item xs={12} key={index}>
                  <Skeleton variant="text" className={classes.formLabel} />
                  <Skeleton
                    variant="rectangular"
                    className={classes.formInput}
                  />
                </Grid>
              );
            })}
          </Grid>
        )}
        {matchesMediumBreakpoint && ( //desktop view
          <Grid container direction="row" className={classes.formContainer}>
            <Grid container direction="row">
              <Grid item xs={6} className={classes.inputContainer}>
                <Skeleton variant="text" className={classes.formLabel} />
                <Skeleton variant="rectangular" className={classes.formInput} />
              </Grid>
              <Grid item xs={6} className={classes.inputContainer}>
                <Skeleton variant="text" className={classes.formLabel} />
                <Skeleton variant="rectangular" className={classes.formInput} />
              </Grid>
            </Grid>
            <Grid container direction="row">
              <Grid item xs={6} className={classes.inputContainer}>
                <Skeleton variant="text" className={classes.formLabel} />
                <Skeleton variant="rectangular" className={classes.formInput} />
              </Grid>
              <Grid item xs={3} className={classes.inputContainer}>
                <Skeleton variant="text" className={classes.formLabel} />
                <Skeleton variant="rectangular" className={classes.formInput} />
              </Grid>
              <Grid item xs={3} className={classes.inputContainer}>
                <Skeleton variant="text" className={classes.formLabel} />
                <Skeleton variant="rectangular" className={classes.formInput} />
              </Grid>
            </Grid>
            <Grid container direction="row">
              <Grid item xs={12} className={classes.inputContainer}>
                <Skeleton variant="text" className={classes.formLabel} />
                <Skeleton variant="rectangular" className={classes.formInput} />
              </Grid>
            </Grid>
          </Grid>
        )}
        <Grid
          container
          direction="row"
          className={classes.disclaimerTextContainer}
        >
          {[...Array(3)].map((value: undefined, index: number) => {
            return (
              <Skeleton
                variant="text"
                key={index}
                className={classes.disclaimerText}
              />
            );
          })}
        </Grid>
        <Skeleton variant="rectangular" className={classes.submitButton} />
      </div>
    </div>
  );
};

export default RequestAQuoteSkeleton;
